import { Box, CircularProgress, Typography } from "@mui/material";
import type { StreamStateValue } from "../types/streamState";
import { hasStoppedMessage, type StreamPreviewUiState } from "../utils/streamPreview";

type StreamPreviewTileProps = {
  title: string;
  previewUrl?: string;
  previewState?: StreamPreviewUiState;
  streamState?: StreamStateValue;
  streamStateMessage?: unknown;
  compact?: boolean;
  showTitleOverlay?: boolean;
  width?: number | string;
  height?: number | string;
  aspectRatio?: string;
};

const StreamPreviewTile = ({
  title,
  previewUrl,
  previewState,
  streamState,
  streamStateMessage,
  compact = false,
  showTitleOverlay = false,
  width = "100%",
  height,
  aspectRatio = "16 / 9"
}: StreamPreviewTileProps) => {
  const isTimeout = streamState === "timeout";
  const isOffline =
    streamState === "offline" || streamState === "unknown" || hasStoppedMessage(streamStateMessage);

  return (
    <Box
      sx={{
        width,
        height,
        ...(height ? {} : { aspectRatio }),
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default"
      }}
    >
      {isOffline ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            gap: compact ? 0 : 0.5
          }}
        >
          <Typography variant={compact ? "caption" : "subtitle2"}>Offline</Typography>
        </Box>
      ) : isTimeout ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "warning.main",
            gap: compact ? 0 : 0.5
          }}
        >
          <Typography variant={compact ? "caption" : "subtitle2"}>Timeout</Typography>
          {!compact ? (
            <Typography variant="body2" color="text.secondary">
              Waiting for stream…
            </Typography>
          ) : null}
        </Box>
      ) : previewUrl ? (
        <Box
          component="img"
          src={previewUrl}
          alt={title}
          loading="lazy"
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            bgcolor: "background.default"
          }}
        />
      ) : previewState === "error" ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            gap: compact ? 0 : 0.6
          }}
        >
          <Typography variant={compact ? "caption" : "body2"}>
            {compact ? "No preview" : "Preview unavailable"}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            gap: compact ? 0 : 0.6
          }}
        >
          <CircularProgress size={compact ? 14 : 22} />
          {!compact ? <Typography variant="body2">Loading preview…</Typography> : null}
        </Box>
      )}
      {showTitleOverlay ? (
        <Box
          sx={{
            position: "absolute",
            inset: "auto 0 0 0",
            px: 1,
            py: 0.1,
            bgcolor: "rgba(0, 0, 0, 0.4)",
            color: "common.white"
          }}
        >
          <Typography variant="caption" noWrap sx={{ fontSize: "0.7rem" }}>
            {title}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default StreamPreviewTile;
